export const MARKING_SCAN_RESULT_EVENT = "chio:native-barcode-scan-result";
export const MARKING_SCAN_CANCEL_EVENT = "chio:native-barcode-scan-cancel";
export const NATIVE_SESSION_BOOTSTRAP_EVENT = "cc_native_session_bootstrap";

export type MarkingNativeWindow = Window & {
    ReactNativeWebView?: {
        postMessage: (message: string) => void;
    };
    __CC_NATIVE_CAPABILITIES__?: {
        supportsMarkingDataMatrixScanner?: boolean;
    };
};

export type MarkingScannerMessage = {
    v: 1;
    type: "open_marking_datamatrix_scanner";
    data: {
        requestId: string;
        sessionToken: string;
        source: "lkfr_marking" | "staff_marking";
    };
};

export type MarkingScanPayload = {
    dataMatrix: string;
    format: "datamatrix";
    requestId: string;
};

export function supportsNativeDataMatrixScanner(target?: MarkingNativeWindow): boolean {
    const current = target ?? (typeof window !== "undefined" ? (window as MarkingNativeWindow) : undefined);

    return !!(
        current?.__CC_NATIVE_CAPABILITIES__?.supportsMarkingDataMatrixScanner === true &&
        current.ReactNativeWebView &&
        typeof current.ReactNativeWebView.postMessage === "function"
    );
}

export function createMarkingRequestId(): string {
    const randomUuid = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : "";

    return randomUuid || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function buildMarkingScannerMessage(
    requestId: string,
    sessionToken: string,
    source: "lkfr_marking" | "staff_marking" = "lkfr_marking",
): MarkingScannerMessage {
    return {
        v: 1,
        type: "open_marking_datamatrix_scanner",
        data: { requestId, sessionToken, source },
    };
}

export function buildMarkingScanPayload(dataMatrix: string, requestId: string): MarkingScanPayload {
    return {
        // Deliberately preserve GS/FNC1 and percent sequences exactly.
        dataMatrix,
        format: "datamatrix",
        requestId,
    };
}

export function buildNativeMarkingDeepLink(
    requestId: string,
    sessionToken: string,
    companyId: string,
    returnUrl: string,
): string {
    const url = new URL("chiochio://marking/scan");
    url.searchParams.set("source", "lkfr_marking");
    url.searchParams.set("requestId", requestId);
    url.searchParams.set("sessionToken", sessionToken);
    url.searchParams.set("companyId", companyId);
    url.searchParams.set("returnUrl", returnUrl);

    return url.toString();
}

function errorCode(error: unknown): string {
    const candidate = error as any;
    const details = candidate?.details ?? candidate?.response?.data ?? candidate;

    for (const value of [details?.error, details?.code, details?.detail, candidate?.code]) {
        if (typeof value === "string" && value !== "") {
            return value.toLowerCase();
        }
    }

    return "";
}

export function markingErrorMessage(error: unknown, operation: "load" | "submit"): string {
    const code = errorCode(error);
    const messages: Array<[string[], string]> = [
        [["marking_gtin_mismatch", "gtin_mismatch"], "Этот DataMatrix относится к другому товару. Проверьте позицию на терминале."],
        [["marking_code_already_used", "code_already_used", "marking_already_used"], "Этот код маркировки уже был использован. Возьмите другую единицу товара."],
        [["marking_session_expired", "session_expired"], "Время сканирования истекло. Вернитесь на терминал и создайте запрос заново."],
        [["marking_session_not_pending", "session_not_pending"], "Запрос уже обработан или отменён. Обновите список."],
        [["marking_session_not_found", "session_not_found"], "Запрос не найден. Возможно, он истёк или был отменён на терминале."],
        [["company_forbidden", "company_not_allowed"], "Нет доступа к салону, для которого терминал запросил код."],
        [["company_term_mapping_missing", "company_required"], "Для выбранного салона не настроена связь с CRM."],
        [["unsupported_format"], "Сканер вернул неподдерживаемый формат. Нужен именно DataMatrix."],
        [["term_marking_unavailable", "term_gateway_unavailable"], "Сервис маркировки временно недоступен. Повторите попытку."],
    ];

    for (const [codes, message] of messages) {
        if (codes.some((knownCode) => code.includes(knownCode))) {
            return message;
        }
    }

    return operation === "load"
        ? "Не удалось загрузить запросы маркировки. Повторите попытку."
        : "Не удалось передать DataMatrix на терминал. Код не потерян — повторите сканирование.";
}
